import { Form } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import { media } from 'utils/rwd';

const selectedControl = css`
  color: ${({ $selected }) => ($selected ? 'var(--bs-primary)' : '#495057')};
  background: ${({ $selected }) => ($selected ? 'var(--bs-primary-5)' : 'transparent')};

  &:hover {
    color: #120e02;
    background: #f1f3f5;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(190, 22, 34, 0.22);
  }
`;

const stackedPanel = css`
  ${media.md.down`
    z-index: ${({ $active }) => ($active ? 1 : 0)};
    grid-area: 1 / 1;
    visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
    pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  `}
`;

export const ErrorFeedback = styled(Form.Control.Feedback)`
  display: block;
  margin-top: 4px;
  color: var(--bs-red);
`;

export const Dropzone = styled.div`
  border: 1px dotted #d2ddec;
  outline: none;
  height: 100px;
  padding: 16px;
  position: relative;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  p {
    font-size: 13px;
    width: 100%;
    margin: 0;
  }
  cursor: pointer;
  &:hover {
    background-color: var(--bs-primary-5);
  }
`;

export const PreviewThumbnailWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 16px 0;
`;

export const PreviewThumbnail = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80%;
  height: auto;
  position: relative;
  border: 1px dashed var(--bs-primary-10);
  padding: 8px;
  border-radius: 4px;
  background-color: var(--bs-primary-5);
  background-opacity: 0.1;

  img {
    max-width: 100%;
    max-height: 100%;
  }

  button {
    position: absolute;
    top: -10px;
    right: -10px;
    border: 1px solid #cccccc;
  }

  ${media.md`
    width: 50%;
  `}
`;

export const CropOverlay = styled.div`
  --crop-editor-max-height: calc(100vh - 124px);

  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 24px 100px;
  background: rgba(0, 0, 0, 0.5);

  ${media.md.down`
    --crop-editor-max-height: calc(
      100vh - ${({ $hasPreview }) => ($hasPreview ? '152px' : '100px')}
    );

    padding: ${({ $hasPreview }) => ($hasPreview ? '76px 12px' : '12px 12px 88px')};
  `}
`;

export const CropEditor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;

  > .ReactCrop,
  img {
    max-width: 100%;
    max-height: var(--crop-editor-max-height);
  }

  img {
    display: block;
  }
`;

export const CropWorkspace = styled.div`
  display: grid;
  width: min(100%, ${({ $hasPreview }) => ($hasPreview ? '1800px' : '1200px')});
  height: 100%;
  min-width: 0;
  min-height: 0;
  grid-template-columns: ${({ $hasPreview }) =>
    $hasPreview ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: ${({ $hasPreview }) => ($hasPreview ? '24px' : '0')};

  ${media.md.down`
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);
    gap: 0;
  `}
`;

export const CropPanel = styled.div`
  display: flex;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  ${stackedPanel}
`;

export const PreviewPanel = styled.div`
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  container: customer-preview / inline-size;
  scrollbar-color: rgba(255, 255, 255, 0.7) transparent;
  scrollbar-width: thin;
  ${stackedPanel}
`;

export const PreviewSurface = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
  align-items: safe center;
  justify-content: center;
`;

export const CropToolbar = styled.div`
  position: fixed;
  left: 50%;
  bottom: 24px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: calc(100vw - 48px);
  min-height: 52px;
  padding: 6px 8px;
  border: 1px solid rgba(18, 14, 2, 0.08);
  border-radius: 999px;
  background: #ffffff;
  box-shadow:
    0 12px 30px rgba(18, 14, 2, 0.16),
    0 2px 8px rgba(18, 14, 2, 0.08);
  transform: translateX(-50%);
  white-space: nowrap;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.xs.down`
    bottom: 12px;
    max-width: calc(100vw - 24px);
  `}
`;

export const CropMode = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  gap: 8px;
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border: 0;
  border-radius: 10px;
  ${selectedControl}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:disabled:hover {
    color: ${({ $selected }) => ($selected ? 'var(--bs-primary)' : '#495057')};
    background: ${({ $selected }) => ($selected ? 'var(--bs-primary-5)' : 'transparent')};
  }
`;

export const RatioShape = styled.span`
  position: relative;
  display: block;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  border: 2px ${({ $manual }) => ($manual ? 'dashed' : 'solid')} currentColor;
  border-radius: 3px;

  ${({ $manual }) =>
    $manual &&
    css`
      &::after {
        position: absolute;
        right: -4px;
        bottom: -4px;
        width: 6px;
        height: 6px;
        border: 1px solid currentColor;
        background: #ffffff;
        content: '';
      }
    `}
`;

export const RatioLabel = styled.span`
  color: currentColor;
  font-family: 'Cerebri Sans', sans-serif;
  font-size: 13px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.1px;
  line-height: 1;
`;

export const CropModeLabel = styled.span`
  color: currentColor;
  font-family: 'Cerebri Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;

export const CropFitMode = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  height: 40px;
  padding: 0 10px;
  border: 0;
  border-radius: 10px;
  font-family: 'Cerebri Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1;
  ${selectedControl}
`;

export const CropToolbarDivider = styled.span`
  width: 1px;
  height: 24px;
  margin: 0 4px;
  background: #e9ecef;
`;

export const CropAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #495057;
  background: transparent;

  &:hover {
    color: #120e02;
    background: #f1f3f5;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(190, 22, 34, 0.22);
  }
`;
